/**
 * @file useStepper
 * @author xuemin02 zhangyibo02
 */

import {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {useInterval} from '@huse/timeout';

import {getLimitedValue, add, subtract} from '../../../utils/mathUtil';

export interface StepperProps {
    min?: number;
    max?: number;
    step?: number;
    value?: number;
    defaultValue?: number;
    onChange?: (value?: number) => void;
}

const STEP_INTERVAL = 200;
const USER_TYPINGS = {
    INCREASE: 'increase',
    DECREASE: 'decrease'
};

export default (props: StepperProps) => {
    const {min, max, step = 1, defaultValue, onChange} = props;
    const initValue = 'value' in props ? props.value : defaultValue;
    const [value, setValue] = useState(initValue);
    const [abort, setAbort]: [boolean, any] = useState(true);

    const userTypingRef = useRef<string>('');
    const timerRef = useRef<number | null>(null);

    const {disabledDecrease, disabledIncrease} = useMemo(() => {
        let disabledDecrease: boolean = false;
        let disabledIncrease: boolean = false;

        if (value || value === 0) {
            const valueInfo = getLimitedValue({value, min, max});
            const relation = valueInfo && valueInfo.relation;
            disabledDecrease = !relation || relation === 'less';
            disabledIncrease = !relation || relation === 'more';
        }

        return {disabledDecrease, disabledIncrease};
    }, [min, max, value]);

    // 增加
    const onIncrease = useCallback(() => setValue((pre: any = 0) => {
        if (step && !disabledIncrease) {
            timerRef.current && clearTimeout(timerRef.current);
            timerRef.current = window.setTimeout(() => {
                userTypingRef.current = USER_TYPINGS.INCREASE;
                setAbort(false);
            }, 500);
            return add(pre, step);
        }
        return pre;
    }), [step, disabledIncrease]);

    // 减少
    const onDecrease = useCallback(() => setValue((pre: any = 0) => {
        if (step && !disabledDecrease) {
            timerRef.current && clearTimeout(timerRef.current);
            timerRef.current = window.setTimeout(() => {
                userTypingRef.current = USER_TYPINGS.DECREASE;
                setAbort(false);
            }, 500);
            return subtract(pre, step);
        }
        return pre;
    }), [step, disabledDecrease]);

    const onStepStop = useCallback(() => {
        timerRef.current && clearTimeout(timerRef.current);
        userTypingRef.current = '';
        setAbort(true);
    }, []);

    const onValueChange = (inputValue: any) => setValue(inputValue);

    const handleStep = useCallback(() => {
        if (userTypingRef.current === USER_TYPINGS.INCREASE) {
            onIncrease();
        }
        if (userTypingRef.current === USER_TYPINGS.DECREASE) {
            onDecrease();
        }
    }, [onDecrease, onIncrease]);

    useInterval(handleStep, abort ? -1 : STEP_INTERVAL);

    useEffect(() => {
        if (disabledDecrease || disabledIncrease) {
            timerRef.current && clearTimeout(timerRef.current);
            setAbort(true);
        }
    }, [disabledDecrease, disabledIncrease]);

    useEffect(() => {
        if (onChange) {
            onChange(value);
        }
    }, [value, onChange]);

    return {value, disabledDecrease, disabledIncrease, onIncrease, onDecrease, onStepStop, onValueChange};
};
